import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
// Api
import { COMPLETE_INSTAGRAM_AUTH } from 'api/auth/mutations';
import { CONNECTED_SOCIAL_ACCOUNTS } from 'api/auth/queries';
import {
  CompleteInstagramAuth,
  CompleteInstagramAuthVariables,
} from 'api/auth/types/CompleteInstagramAuth';
import { SocialMediaConnectionProvider } from 'api/graphql-global-types';
import { GetFacebookPages } from 'api/social-media-connections/types/GetFacebookPages';
// Helpers
import {
  createSocialIntegrationStorage,
  SocialIntegrationState,
  SOCIAL_INTEGRATION_PROVIDER_KEY,
  SOCIAL_INTEGRATION_STATE_KEY,
} from 'helpers/socialIntegrationStorage';
import {
  getSocialIntegrationErrorDialog,
  SocialIntegrationErrorDialogData,
} from 'helpers/social-integration-errors';
// Hooks
import { useGetFacebookPages } from './useGetFacebookPages';
import { useCompleteFacebookAuth } from './useCompleteFacebookAuth';
import { useCompleteTiktokIntegrationAuth } from './useCompleteTiktokIntegrationAuth';
import { showToast } from 'components/common/Toast/Toast';

// The fields and return listener are separate hook instances. Coordinate each flow across both.
const integrationsStartedOnPage = new Set<string>();
const integrationsBeingRestored = new Set<string>();
const authorizationResults = new Map<
  string,
  Promise<SocialIntegrationCallbackResult>
>();
const pendingProviders = new Map<
  string,
  SocialMediaConnectionProvider | null
>();
const pendingProviderListeners = new Set<() => void>();
const setSharedPendingProvider = (
  providerKey: string,
  provider: SocialMediaConnectionProvider | null
) => {
  pendingProviders.set(providerKey, provider);
  pendingProviderListeners.forEach((listener) => listener());
};

export type SocialIntegrationCallbackResult = {
  facebookPages?: GetFacebookPages;
  errorDialog?: SocialIntegrationErrorDialogData;
};

type RestorePendingIntegrationOptions = {
  clearCallbackQuery?: boolean;
  onAuthorized?: (
    result: SocialIntegrationCallbackResult
  ) => void | Promise<void>;
};

type UseSocialIntegrationListenerArgs = {
  stateKey?: string;
  providerKey?: string;
};
type RestoredSocialIntegration<T> = T & {
  codeResponse?: GetFacebookPages;
  errorDialog?: SocialIntegrationErrorDialogData;
  files?: Record<string, File>;
};

const getAuthorizationCode = (query: {
  auth_code?: string | string[];
  code?: string | string[];
}) =>
  typeof query.auth_code === 'string'
    ? query.auth_code
    : typeof query.code === 'string'
    ? query.code
    : undefined;

export const useSocialIntegrationListener = ({
  stateKey = SOCIAL_INTEGRATION_STATE_KEY,
  providerKey = SOCIAL_INTEGRATION_PROVIDER_KEY,
}: UseSocialIntegrationListenerArgs = {}) => {
  const { query, replace, isReady } = useRouter();
  const [pageRestoreCount, setPageRestoreCount] = useState(0);
  const [
    pendingProvider,
    setPendingProviderState,
  ] = useState<SocialMediaConnectionProvider | null>(
    () => pendingProviders.get(providerKey) ?? null
  );
  useEffect(() => {
    const syncPendingProvider = () =>
      setPendingProviderState(pendingProviders.get(providerKey) ?? null);
    pendingProviderListeners.add(syncPendingProvider);
    syncPendingProvider();
    return () => {
      pendingProviderListeners.delete(syncPendingProvider);
    };
  }, [providerKey]);
  const setPendingProvider = useCallback(
    (provider: SocialMediaConnectionProvider | null) =>
      setSharedPendingProvider(providerKey, provider),
    [providerKey]
  );
  const storage = useMemo(
    () => createSocialIntegrationStorage(stateKey, providerKey),
    [stateKey, providerKey]
  );

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      // Back/forward cache restores the same document without remounting React.
      if (event.persisted) setPageRestoreCount((count) => count + 1);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const apolloClient = useApolloClient();
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState !== 'visible' ||
        !integrationsStartedOnPage.has(providerKey) ||
        storage.getPending()
      )
        return;
      // The attempt was finished in the tab the provider app opened, so show its result here.
      integrationsStartedOnPage.delete(providerKey);
      apolloClient
        .refetchQueries({ include: [CONNECTED_SOCIAL_ACCOUNTS] })
        .catch(() => undefined);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [apolloClient, providerKey, storage]);

  const [completeInstagramAuth] = useMutation<
    CompleteInstagramAuth,
    CompleteInstagramAuthVariables
  >(COMPLETE_INSTAGRAM_AUTH, {
    refetchQueries: [{ query: CONNECTED_SOCIAL_ACCOUNTS }],
    awaitRefetchQueries: true,
  });
  const [completeFacebookAuth] = useCompleteFacebookAuth();
  const [completeTiktokIntegrationAuth] = useCompleteTiktokIntegrationAuth();
  const [getFacebookPages] = useGetFacebookPages();

  const completeAuthorization = useCallback(
    async (
      code: string,
      provider: string,
      redirectUrl: string
    ): Promise<GetFacebookPages | undefined> => {
      const variables = { input: { code, redirectUrl } };
      if (provider === SocialMediaConnectionProvider.Instagram) {
        await completeInstagramAuth({ variables });
        return;
      }
      if (provider === SocialMediaConnectionProvider.Tiktok) {
        await completeTiktokIntegrationAuth({ variables });
        return;
      }
      if (provider !== SocialMediaConnectionProvider.Facebook) {
        throw new Error('Unsupported social integration provider');
      }
      const { data, error } = await getFacebookPages({ variables });
      if (error) throw error;
      if (!data) throw new Error('Facebook did not return any account data');
      const { pages, sessionId } = data.getFacebookPages;
      if (pages.length === 1) {
        await completeFacebookAuth({
          variables: { input: { pageId: pages[0].id, sessionId } },
        });
        return;
      }
      return data;
    },
    [
      completeInstagramAuth,
      completeTiktokIntegrationAuth,
      completeFacebookAuth,
      getFacebookPages,
    ]
  );

  const restorePendingIntegration = useCallback(
    async <T extends SocialIntegrationState>(
      onRestore: (state: RestoredSocialIntegration<T>) => void | Promise<void>,
      redirectUrl: string,
      options: RestorePendingIntegrationOptions = {}
    ) => {
      if (typeof window === 'undefined' || !isReady) return;
      if (
        integrationsStartedOnPage.has(providerKey) ||
        integrationsBeingRestored.has(providerKey)
      )
        return;
      integrationsBeingRestored.add(providerKey);
      try {
        const pending = storage.getPending();
        if (!pending) return;
        const { provider } = pending;
        const code = getAuthorizationCode(query);
        const isProviderCallback = Boolean(
          code || query.error || query.error_reason || query.error_description
        );
        // Another tab may only take the attempt over with the provider's callback,
        // otherwise any open tab would restore (and clear) it while the user is away.
        if (!pending.startedInThisTab && !isProviderCallback) return;
        const savedIntegration = await storage.read<T>();
        const restoredState = {
          ...savedIntegration.state,
          ...(savedIntegration.files ? { files: savedIntegration.files } : {}),
        };
        const restoreBeforeAuthorization = Boolean(options.onAuthorized);
        if (restoreBeforeAuthorization) await onRestore(restoredState);
        const authorizationKey = `${providerKey}:${code}`;
        let callbackResult: SocialIntegrationCallbackResult | undefined;
        if (code) {
          setPendingProvider(provider as SocialMediaConnectionProvider);
          // Codes are single-use. Retain the result if restoring the form needs another attempt.
          let authorization = authorizationResults.get(authorizationKey);
          if (!authorization) {
            authorization = completeAuthorization(code, provider, redirectUrl)
              .then((facebookPages) => ({ facebookPages }))
              .catch((error) => {
                const errorDialog = getSocialIntegrationErrorDialog(error);
                if (!errorDialog) {
                  showToast({
                    type: 'error',
                    message:
                      'Unable to link your account. Your form has been restored. Please try linking again.',
                  });
                }
                return { errorDialog: errorDialog ?? undefined };
              });
            authorizationResults.set(authorizationKey, authorization);
          }
          callbackResult = await authorization;
        }
        if (restoreBeforeAuthorization) {
          if (callbackResult) await options.onAuthorized?.(callbackResult);
        } else {
          await onRestore({
            ...restoredState,
            codeResponse: callbackResult?.facebookPages,
            errorDialog: callbackResult?.errorDialog,
          });
        }
        await savedIntegration.clear();
        authorizationResults.delete(authorizationKey);
        if (options.clearCallbackQuery !== false && isProviderCallback) {
          const nextQuery = { ...query };
          delete nextQuery.code;
          delete nextQuery.auth_code;
          delete nextQuery.scopes;
          delete nextQuery.error;
          delete nextQuery.error_reason;
          delete nextQuery.error_description;
          await replace({ query: nextQuery }, undefined, { shallow: true });
        }
      } catch {
        showToast({
          type: 'error',
          message:
            'Unable to restore your saved form. Please reload the page to try again.',
        });
      } finally {
        setPendingProvider(null);
        integrationsBeingRestored.delete(providerKey);
      }
    },
    // `pageRestoreCount` is unused in the body on purpose: bumping it is what re-creates this
    // callback, which is how consumers re-run the restore after a back/forward cache page show.
    // Removing it to satisfy the rule silently breaks restoring on Back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isReady,
      providerKey,
      storage,
      query,
      completeAuthorization,
      replace,
      setPendingProvider,
      pageRestoreCount,
    ]
  );

  const savePendingIntegration = useCallback(
    async <T extends SocialIntegrationState>(
      state: T,
      provider: SocialMediaConnectionProvider,
      files?: Record<string, File>
    ) => {
      await storage.save(state, provider, files);
      // Keep the attempt pending while href navigation is being scheduled, even if React rerenders.
      integrationsStartedOnPage.add(providerKey);
      window.addEventListener(
        'pagehide',
        () => integrationsStartedOnPage.delete(providerKey),
        { once: true }
      );
    },
    [providerKey, storage]
  );

  return {
    restorePendingIntegration,
    savePendingIntegration,
    pendingProvider,
  };
};
